import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  ListItemIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import UserMenu from "../components/UserMenu";
import SettingsMenu from "../components/SettingsMenu";
import { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useRoles } from "../useRoles";
import { colors, spacing, shadows, components, typography, elevation } from "../theme";
import { NAVIGATION_CONFIG } from "../config/api";
import EnvironmentBanner from "../components/EnvironmentBanner";
import OCLogoOutlined from "../assets/OCLogoOutlined.svg";
import OCLogoFullOutlined from "../assets/OCLogoFullOutlined.svg";
const drawerWidth = components.layout.drawerWidth.expanded;
const collapsedWidth = components.layout.drawerWidth.collapsed;

export default function MainLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const isSuperUser = useRoles().includes("SuperUser");

  const { instance, accounts } = useMsal();
  const username = accounts[0]?.name || accounts[0]?.username || "";
  const navigate = useNavigate();
  const location = useLocation();

  const currentDrawerWidth = collapsed ? collapsedWidth : drawerWidth;

  const menuItems = [
    { text: "Customers", icon: <PeopleIcon />, path: NAVIGATION_CONFIG.ROUTES.CUSTOMERS },
    { text: "Shared Addresses", icon: <HomeWorkIcon />, path: NAVIGATION_CONFIG.ROUTES.CARE_HOMES },
  ];

  const managerItems = [
    {
      text: "Duplicate audit",
      icon: <ContentCopyIcon />,
      path: NAVIGATION_CONFIG.ROUTES.MANAGER_DUPLICATE_AUDITS,
    },
  ];
  const managerActive = managerItems.some((item) => location.pathname.startsWith(item.path));

  // Helper function to check if a menu item is active
  const isActive = (itemPath: string) => {
    return location.pathname === itemPath || location.pathname.startsWith(itemPath + '/');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    const logoutRequest = {
      postLogoutRedirectUri: window.location.origin + NAVIGATION_CONFIG.ROUTES.SIGNIN,
      mainWindowRedirectUri: window.location.origin + NAVIGATION_CONFIG.ROUTES.SIGNIN,
    };
    await instance.logoutRedirect(logoutRequest);
  };

  const drawer = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        paddingLeft: spacing.mui.lg,
        paddingRight: spacing.mui.lg,
        paddingTop: spacing.mui.xxxl,
        paddingBottom: spacing.mui.xxxl,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: spacing.mui.xxxl,
        display: "flex",
      }}
    >
      {/* Logo/Brand section */}
      <Box sx={{ 
        alignSelf: 'stretch', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        display: 'flex',
        minHeight: 48
      }}>
        {/* Logo container */}
        <Box 
          sx={{ 
            alignSelf: collapsed ? 'center' : 'stretch',
            width: collapsed ? 48 : 'auto', 
            height: 64, 
            background: colors.background.paper, 
            borderRadius: spacing.mui.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <img 
              src={OCLogoOutlined} 
              alt="OC Logo" 
              style={{ 
                width: 48, 
                height: 48
              }} 
            />
          ) : (
            <img 
              src={OCLogoFullOutlined} 
              alt="OC Logo Full" 
              style={{ 
                height: 48,
                width: 'auto',
                maxWidth: '160px'
              }} 
            />
          )}
        </Box>
        
        {/* Collapse button - only show when expanded */}
        {!collapsed && (
          <Tooltip title="Collapse" placement="right">
            <IconButton
              onClick={() => setCollapsed(!collapsed)}
              sx={{ color: colors.brand.purple, flexShrink: 0 }}
            >
              <ChevronLeftIcon sx={{ fontSize: typography.fontSize.xxxl, fontWeight: typography.fontWeight.medium }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Main navigation */}
      <Box
        sx={{
          alignSelf: "stretch",
          flex: "1 1 0",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: 4,
          display: "flex",
        }}
      >
        <Box
          sx={{
            alignSelf: "stretch",
            flex: "1 1 0",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: 4,
            display: "flex",
          }}
        >
          <Box
            sx={{
              alignSelf: "stretch",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              display: "flex",
            }}
          >
            {menuItems.map((item) => (
              <Tooltip
                key={item.text}
                title={collapsed ? item.text : ""}
                placement="right"
              >
                <Box
                  onClick={() => navigate(item.path)}
                  sx={{
                    alignSelf: 'stretch',
                    padding: spacing.mui.lg,
                    paddingLeft: spacing.mui.lg,
                    paddingRight: spacing.mui.lg,
                    background: isActive(item.path) ? colors.primary.main : 'transparent',
                    borderRadius: spacing.mui.sm,
                    justifyContent: collapsed ? "center" : "flex-start",
                    alignItems: "center",
                    gap: collapsed ? 0 : 1,
                    display: "flex",
                    cursor: "pointer",
                    minWidth: collapsed ? 48 : "auto",
                    "&:hover": {
                      backgroundColor:
                        isActive(item.path)
                          ? colors.primary.hover
                          : colors.interaction.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      color:
                        isActive(item.path)
                          ? colors.text.white
                          : colors.text.primary,
                      justifyContent: "center",
                      width: 16,
                      height: 16,
                      marginRight: collapsed ? 0 : 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <Typography
                      sx={{
                        textAlign: "left",
                        color:
                          isActive(item.path)
                            ? colors.text.white
                            : colors.text.primary,
                        fontSize: typography.fontSize.medium,
                        fontFamily: typography.fontFamily.primary,
                        fontWeight: typography.fontWeight.bold,
                        lineHeight: typography.lineHeight.tight,
                        whiteSpace: "nowrap",
                        minWidth: "max-content",
                        flex: 1,
                      }}
                    >
                      {item.text}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            ))}

            {/* Manager group - Super Users only */}
            {isSuperUser && (
              <>
                <Tooltip title={collapsed ? "Manager" : ""} placement="right">
                  <Box
                    onClick={() => {
                      if (collapsed) setCollapsed(false);
                      setManagerOpen(collapsed ? true : !managerOpen);
                    }}
                    sx={{
                      alignSelf: "stretch",
                      padding: spacing.mui.lg,
                      borderRadius: spacing.mui.sm,
                      justifyContent: collapsed ? "center" : "flex-start",
                      alignItems: "center",
                      gap: collapsed ? 0 : 1,
                      display: "flex",
                      cursor: "pointer",
                      minWidth: collapsed ? 48 : "auto",
                      "&:hover": { backgroundColor: colors.interaction.hover },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        color: managerActive ? colors.primary.main : colors.text.primary,
                        justifyContent: "center",
                        width: 16,
                        height: 16,
                        marginRight: collapsed ? 0 : 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ManageAccountsIcon />
                    </ListItemIcon>
                    {!collapsed && (
                      <>
                        <Typography
                          sx={{
                            textAlign: "left",
                            color: managerActive ? colors.primary.main : colors.text.primary,
                            fontSize: typography.fontSize.medium,
                            fontFamily: typography.fontFamily.primary,
                            fontWeight: typography.fontWeight.bold,
                            lineHeight: typography.lineHeight.tight,
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                        >
                          Manager
                        </Typography>
                        {managerOpen ? <ExpandLess /> : <ExpandMore />}
                      </>
                    )}
                  </Box>
                </Tooltip>
                {!collapsed &&
                  (managerOpen || managerActive) &&
                  managerItems.map((item) => (
                    <Box
                      key={item.text}
                      onClick={() => navigate(item.path)}
                      sx={{
                        alignSelf: "stretch",
                        padding: spacing.mui.lg,
                        paddingLeft: spacing.mui.xxxl,
                        background: isActive(item.path) ? colors.primary.main : "transparent",
                        borderRadius: spacing.mui.sm,
                        alignItems: "center",
                        gap: 1,
                        display: "flex",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: isActive(item.path)
                            ? colors.primary.hover
                            : colors.interaction.hover,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          color: isActive(item.path) ? colors.text.white : colors.text.primary,
                          width: 16,
                          height: 16,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <Typography
                        sx={{
                          color: isActive(item.path) ? colors.text.white : colors.text.primary,
                          fontSize: typography.fontSize.medium,
                          fontFamily: typography.fontFamily.primary,
                          fontWeight: typography.fontWeight.bold,
                          lineHeight: typography.lineHeight.tight,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
              </>
            )}
          </Box>
        </Box>

        {/* Bottom menu items */}
        <Box
          sx={{
            alignSelf: "stretch",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            display: "flex",
          }}
        >
          <SettingsMenu hideLabel={collapsed} />
          <UserMenu
            username={username}
            onLogout={handleLogout}
            hideLabel={collapsed}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <CssBaseline />
      <EnvironmentBanner />
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          bgcolor: colors.background.default,
        }}
      >

        {/* Mobile menu button */}
        <Box
          sx={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 1200,
            display: { sm: "none" },
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              bgcolor: "background.paper",
              boxShadow: elevation.minimal,
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 32, fontWeight: "bold" }} />
          </IconButton>
        </Box>

        {/* Navigation */}
        <Box
          component="nav"
          sx={{
            width: { sm: currentDrawerWidth },
            flexShrink: { sm: 0 },
            "& .MuiDrawer-paper": {
              width: currentDrawerWidth,
              boxSizing: "border-box",
            },
          }}
        >
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                width: currentDrawerWidth,
                bgcolor: "background.paper",
              },
            }}
          >
            {drawer}
          </Drawer>

          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                position: "relative",
                width: currentDrawerWidth,
                height: "calc(100vh - 48px)",
                margin: "24px 16px 24px 24px",
                borderRadius: "5px",
                border: "none",
                backgroundColor: colors.background.paper,
                boxShadow: shadows.drawer,
                transition: "width 0.3s ease",
                overflowX: "hidden",
                overflowY: "auto",
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          pt: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          transition: "width 0.3s ease",
          alignSelf: "flex",
        }}
      >
        <Outlet />
      </Box>
      </Box>
    </>
  );
}
